/**
 * Holo tilt for TCG singles: on hover the card image tilts in 3D toward the pointer. Foil cards also get a rainbow
 * foil shine that moves with the pointer; other singles get a soft light glare.
 *
 * Which products get it is decided in snippets/holo-kind.liquid, which leaves a hidden marker next to the image:
 * - product cards (blocks/_product-card-gallery.liquid): a [data-holo] marker inside the <product-card>. Only the
 *   card's picture tilts, not the whole card gallery, so the add to cart button and the badges on top of it stay put
 *   and easy to click. The picture takes the gallery's rounded corners and the gallery stops clipping, so the tilted
 *   picture isn't cut off at the edges;
 * - product pages (blocks/_product-media-gallery.liquid): a [data-holo] marker next to the <media-gallery>.
 * Hover is picked up for the whole document, so cards added later (filters, infinite scroll) work too.
 *
 * Only on devices with a mouse, and not for anyone who asks their device to reduce motion.
 */

const MAX_TILT_X = 7.5;
const MAX_TILT_Y = 9.5;

const style = document.createElement('style');
style.textContent = `
  .holo-target {
    position: relative;
    transition: transform 0.5s cubic-bezier(0.2, 0.7, 0.2, 1);
  }

  .holo-target.holo-on {
    z-index: 2;
    transform: perspective(900px) rotateX(var(--holo-rx, 0deg)) rotateY(var(--holo-ry, 0deg)) scale(1.025);
    transition: transform 0.08s linear;
  }

  .holo-glare {
    position: absolute;
    inset: 0;
    z-index: 3;
    border-radius: inherit;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.3s ease;
  }

  .holo-on > .holo-glare {
    opacity: 1;
  }

  .holo-glare--plain {
    background: radial-gradient(circle at var(--holo-x, 50%) var(--holo-y, 50%), rgb(255 255 255 / 0.34), transparent 55%);
    mix-blend-mode: soft-light;
  }

  .holo-glare--foil {
    background:
      radial-gradient(circle at var(--holo-x, 50%) var(--holo-y, 50%), rgb(255 255 255 / 0.4), transparent 42%),
      linear-gradient(
        115deg,
        transparent 12%,
        rgb(255 60 170 / 0.45) 28%,
        rgb(255 220 60 / 0.45) 40%,
        rgb(60 255 190 / 0.45) 52%,
        rgb(60 150 255 / 0.45) 64%,
        rgb(190 80 255 / 0.45) 76%,
        transparent 90%
      );
    background-size: 100% 100%, 300% 300%;
    background-position: center, var(--holo-x, 50%) var(--holo-y, 50%);
    mix-blend-mode: color-dodge;
  }

  .holo-on > .holo-glare--foil {
    opacity: 0.6;
  }

  /* Product cards: the picture tilts inside the gallery, which no longer clips it; buttons and badges stay on top */
  .card-gallery.holo-frame {
    overflow: visible;
  }

  .card-gallery.holo-frame > :is(quick-add-component, .product-badges) {
    z-index: 3;
  }
`;

/**
 * Finds the image to tilt under the pointer, and its effect.
 * @param {EventTarget | null} node
 * @returns {{ target: HTMLElement, kind: string } | null}
 */
function findTarget(node) {
  if (!(node instanceof Element)) return null;

  const gallery = node.closest('.card-gallery');
  if (gallery) {
    // Hovering the add to cart button (or a badge) doesn't tilt anything
    if (node.closest('quick-add-component, .product-badges')) return null;
    const marker = gallery.closest('product-card')?.querySelector('[data-holo]');
    if (!marker) return null;
    const picture = /** @type {HTMLElement | null} */ (
      gallery.querySelector(':scope > slideshow-component, :scope > a > .product-media-container, .product-media-container')
    );
    if (!picture) return null;
    if (!gallery.classList.contains('holo-frame')) {
      picture.style.borderRadius = getComputedStyle(gallery).borderRadius;
      picture.style.overflow = 'hidden';
      gallery.classList.add('holo-frame');
    }
    return { target: picture, kind: marker.getAttribute('data-holo') || 'plain' };
  }

  const media = node.closest('media-gallery .product-media');
  if (media) {
    const mediaGallery = media.closest('media-gallery');
    const marker = mediaGallery?.parentElement?.querySelector(':scope > [data-holo]');
    return marker ? { target: /** @type {HTMLElement} */ (media), kind: marker.getAttribute('data-holo') || 'plain' } : null;
  }

  return null;
}

/** @param {{ target: HTMLElement, kind: string }} found */
function start({ target, kind }) {
  if (target.classList.contains('holo-on')) return;

  if (!target.querySelector(':scope > .holo-glare')) {
    const glare = document.createElement('span');
    glare.className = `holo-glare holo-glare--${kind}`;
    glare.setAttribute('aria-hidden', 'true');
    target.append(glare);
  }

  target.classList.add('holo-target', 'holo-on');

  /** @param {PointerEvent} event */
  const move = (event) => {
    const box = target.getBoundingClientRect();
    const x = Math.min(Math.max((event.clientX - box.left) / box.width, 0), 1);
    const y = Math.min(Math.max((event.clientY - box.top) / box.height, 0), 1);
    target.style.setProperty('--holo-rx', `${((0.5 - y) * 2 * MAX_TILT_X).toFixed(2)}deg`);
    target.style.setProperty('--holo-ry', `${((x - 0.5) * 2 * MAX_TILT_Y).toFixed(2)}deg`);
    target.style.setProperty('--holo-x', `${(x * 100).toFixed(1)}%`);
    target.style.setProperty('--holo-y', `${(y * 100).toFixed(1)}%`);
  };

  const leave = () => {
    target.classList.remove('holo-on');
    target.removeEventListener('pointermove', move);
    for (const name of ['--holo-rx', '--holo-ry']) target.style.removeProperty(name);
  };

  target.addEventListener('pointermove', move);
  target.addEventListener('pointerleave', leave, { once: true });
}

if (
  window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
  !window.matchMedia('(prefers-reduced-motion: reduce)').matches
) {
  document.head.append(style);
  document.addEventListener('pointerover', (event) => {
    const found = findTarget(event.target);
    if (found) start(found);
  });
}